import { Dispatch } from 'react-redux';
import { Api } from '../typings/api';
import { DRIVE_ITEM_LIST_PAGE_SIZE } from '../containers/DriveItemList/constants';
import { OfflineManagerState, DownloadStatus } from '../containers/OfflineManager/reducer';
import { downloadThumb, updateThumbStatus } from '../containers/OfflineManager/actions';
import Utils from '.';
import UserAgentUtils from './userAgent';
import UrlUtils from './url';
import { StoreState } from '../reducers';
import { DownloadPriority } from './nativeApi';
const I18n = require('react-redux-i18n').I18n;

const minimumWidth = 360;

export enum ResolutionDisplayType {
	Card = 'Card',
	Detail = 'Detail',
	Theater = 'Theater'
}

export enum ActionButton {
	Read = 'read',
	Download = 'download',
	Update = 'update',
	Delete = 'delete',
	Cancel = 'cancel',
	Required = 'required'
}

export enum ActionButtonStyle {
	Bar = 'Bar',
	Icon = 'Icon',
	IconBold = 'IconBold',
	ParametersPage = 'ParametersPage'
}


interface ThumbnailDescriptor {
	isImage: boolean;
	src: string;
}

export default class DriveItemUtils {

	static getLabel(driveItem: Api.DriveItem): string {
		const { titles } = driveItem;
		const titleKeys = Object.keys(titles);
		return !!titleKeys.length ? titles[titleKeys[0]] : driveItem.label;
	}

	static getLocale(driveItem: Api.DriveItem): string {
		const { titles, descriptions } = driveItem;
		const titleKeys = Object.keys(titles);
		const descriptionKeys = Object.keys(descriptions);
		return titleKeys[0] || descriptionKeys[0] || '';
	}

	static getExtension(driveItem: Api.DriveItem): string {
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.Gallery:
				return '';
			case Api.DriveItemType.Publication:
				return I18n.t(`messages.extension_flip`);
			case Api.DriveItemType.ExternalLink:
				return I18n.t(`messages.extension_external_link`);
		}

		if (driveItem.originalFile) {
			return Utils.getFileExtension(driveItem.originalFile);
		}
		return '';
	}

	static isPreviewable(driveItem: Api.DriveItem): boolean {
		return [Api.DriveItemType.Image, Api.DriveItemType.Movie, Api.DriveItemType.Sound].includes(driveItem.type);
	}

	static getThumbnailDescriptor = (
		driveItem: Api.DriveItem,
		isBox: boolean,
		offlineManager: OfflineManagerState,
		config: Api.Config,
		resolutions: { [k: string]: Api.ResolutionGroup},
		dispatch: Dispatch<StoreState>
	): ThumbnailDescriptor => {
		if (UserAgentUtils.isMobileApp) {
			const driveItemStatus = offlineManager.thumbMap[driveItem.id];
			if (driveItemStatus && driveItemStatus.status === DownloadStatus.DOWNLOADED) {
				const src = Utils.getDeviceThumbnailPath(config, driveItem, resolutions, false);
				return { isImage: true, src };
			} else if (DriveItemUtils.hasIcon(driveItem)
				&& !(driveItemStatus && (driveItemStatus.status === DownloadStatus.DOWNLOADING || driveItemStatus.status === DownloadStatus.ERROR))
			) {
				dispatch(downloadThumb(driveItem.id, DownloadPriority.Normal, false));
				dispatch(updateThumbStatus(driveItem.id, driveItem.version, DownloadStatus.DOWNLOADING));
			}
		} else if (DriveItemUtils.hasIcon(driveItem)) {
			const src = DriveItemUtils.getThumbnail(driveItem, config, resolutions, isBox);
			return { isImage: true, src };
		}
		return { isImage: false, src: DriveItemUtils.getDefaultThumbnail(driveItem) };
	}

	static getThumbnail(driveItem: Api.DriveFolderInfo, galleryConfig: Api.Config, globalResolutions: { [k: string]: Api.ResolutionGroup }, isBox: boolean): string {
		// fallback order iconWithResolution > iconOriginalUrl > defaultIcon
		const resolutions = driveItem.resolutions || globalResolutions;
		const { icon } = driveItem;
		if (icon) {
			const iconId = this.getIconId(driveItem);

			if (iconId < 0) {
				return icon.replace('$media$', `${UrlUtils.getRootApiClient(galleryConfig)}/`);
			}

			const imageWidth = parseInt(DriveItemUtils.getImageWidth(driveItem), 10);
			const targetWidth = DriveItemUtils.getTargetWidth(ResolutionDisplayType.Card);

			if (driveItem.associatedResolutions && imageWidth >= targetWidth) {
				const availableResolutionsId = driveItem.associatedResolutions[iconId] || 'default';
				const availableResolutions = resolutions[availableResolutionsId];

				if (availableResolutions !== undefined && DriveItemUtils.isSupportedExtension(driveItem, resolutions)) {
					const resolution = DriveItemUtils.getIdealSizeFromResolutions(availableResolutions, ResolutionDisplayType.Card, isBox);
					return this.getImagePathByResolution(driveItem, galleryConfig, resolution);
				}
			}
			return this.getIconOriginalPath(driveItem, galleryConfig);
		}
		return this.getDefaultThumbnail(driveItem);
	}

	static getTheaterImage(driveItem: Api.DriveFolderInfo, galleryConfig: Api.Config, globalResolutions: { [k: string]: Api.ResolutionGroup }): string {
		const resolutions = driveItem.resolutions || globalResolutions;
		const imageWidth = parseInt(DriveItemUtils.getImageWidth(driveItem), 10);
		const targetWidth = DriveItemUtils.getTargetWidth(ResolutionDisplayType.Theater);
		const extension = Utils.getFileExtension(driveItem.originalFile);
		const isSvg = extension === 'svg';
		const isGif = extension === 'gif';

		if (!driveItem.associatedResolutions || imageWidth < targetWidth || isSvg || isGif) {
			return UrlUtils.getItemOriginalFileUrl(galleryConfig, driveItem.originalFile);
		}

		const availableResolutionsId = driveItem.associatedResolutions[driveItem.id] || 'default';
		const availableResolutions = resolutions[availableResolutionsId];

		const resolution = DriveItemUtils.getIdealSizeFromResolutions(availableResolutions, ResolutionDisplayType.Theater, true);
		return this.getImagePathByResolution(driveItem, galleryConfig, resolution);
	}

	static hasIcon(driveItem: Api.DriveItem): boolean {
		return !!driveItem.icon;
	}

	static getIconId(driveItem: Api.DriveItem): number {
		if (!driveItem.icon) {
			return -1;
		}
		const regexMatches = /^\$media\$([0-9]+)\..*$/.exec( driveItem.icon );
		return regexMatches !== null
			? parseInt(regexMatches[1], 10)
			: -1;
	}

	static isSupportedExtension(driveItem: Api.DriveItem, resolutions: { [k: string]: Api.ResolutionGroup}): boolean {
		const resolvedResolution = driveItem.associatedResolutions ? resolutions[driveItem.associatedResolutions[driveItem.id]] : resolutions.default;
		if (resolvedResolution) {
			const supportedExtensions = resolvedResolution.supported;
			const iconExtension = DriveItemUtils.getIconExtension(driveItem);
			return supportedExtensions.includes(iconExtension);
		}
		return false;
	}

	static getIconExtension(driveItem: Api.DriveItem): string {
		if (driveItem.icon) {
			const extensionRegex = /(?:\.([^.]+))?$/;
			const regexMatch = extensionRegex.exec(driveItem.icon);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}
		return '';
	}

	static getDownloadedFileName(driveItem: Api.DriveItem): string {
		const extension = Utils.getFileExtension(driveItem.originalFile);
		let fileName;

		if (extension === 'tif') {
			fileName = `${driveItem.id}.jpg`;
		} else {
			fileName = `${driveItem.id}.${extension}`;
		}

		return fileName;
	}

	static getTargetWidth(type: ResolutionDisplayType): number {
		return type === ResolutionDisplayType.Theater ? Math.max(window.innerHeight, window.innerWidth) : minimumWidth;
	}

	static getImageWidth(driveItem: Api.DriveItem): string {
		if (driveItem.resolution) {
			const widthRegex = /(\d+)x/;
			const regexMatch = widthRegex.exec(driveItem.resolution);
			return !!regexMatch && !!regexMatch[1] ? regexMatch[1] : '';
		}

		return '';
	}

	static getIdealSizeFromResolutions(availableResolutions: Api.ResolutionGroup, type: ResolutionDisplayType, isBox: boolean): Api.ResolutionSize {
		const targetWidth = DriveItemUtils.getTargetWidth(type);

		// Try to find an associated box that fits
		let resolvedSize = this.getIdealSizeFromSizes(availableResolutions.sizes, targetWidth, isBox);

		// If no box fit, we ignore the isBox parameter
		resolvedSize = resolvedSize || this.getIdealSizeFromSizes(availableResolutions.sizes, targetWidth);

		// If still no box fit, we use the biggest resolution
		if (!resolvedSize) {
			for (const metaSize of availableResolutions.sizes) {
				if (!resolvedSize || metaSize.width > resolvedSize.width) {
					resolvedSize = metaSize;
				}
			}
		}

		return <Api.ResolutionSize> resolvedSize;
	}

	static getIdealSizeFromSizes(metaSizes: Api.ResolutionSize[], targetWidth: number, isBox?: boolean): Api.ResolutionSize | undefined {
		let resolvedSize: Api.ResolutionSize | undefined;

		for (const metaSize of metaSizes) {
			if ((typeof isBox === 'undefined' || metaSize.box === isBox)
			&& (metaSize.width >= targetWidth && (!resolvedSize || metaSize.width < resolvedSize.width))) {
				resolvedSize = metaSize;
			}
		}
		return resolvedSize;
	}

	static getImagePathByResolution(driveItem: Api.DriveItem, galleryConfig: Api.Config, size: Api.ResolutionSize): string {
		const extension = size.extension || (driveItem.type === Api.DriveItemType.Image ? this.getExtension(driveItem) : 'jpg');
		return UrlUtils.getImageUrl(galleryConfig, driveItem, size, extension);
	}

	static getIconOriginalName(driveItem: Api.DriveItem): string {
		if (!driveItem.icon) {
			return '';
		}

		return this.getAssetOriginalName(driveItem.icon);
	}

	static getAssetOriginalName(ressourceUrl: string): string {
		const regexMatches = /^\$media\$(.+)$/.exec(ressourceUrl );
		return regexMatches !== null
			? regexMatches[1]
			: '';
	}

	static getIconOriginalPath(driveItem: Api.DriveItem, galleryConfig: Api.Config): string {
		return this.getAssetOriginalPath(galleryConfig, this.getIconOriginalName(driveItem));
	}

	static getAssetOriginalPath(galleryConfig: Api.Config, ressourceName: string) {
		return UrlUtils.getItemOriginalFileUrl(galleryConfig, ressourceName);
	}

	static getDefaultThumbnail(driveItem: Api.DriveItem): string {
		switch (driveItem.type) {
			case Api.DriveItemType.Directory:
				return 'iconset_folder-bold';
			case Api.DriveItemType.Publication:
				return 'iconset_publication';
			case Api.DriveItemType.File:
				return 'iconset_file';
			case Api.DriveItemType.Image:
				return 'iconset_image-bold';
			case Api.DriveItemType.Movie:
				return 'iconset_video-bold';
			case Api.DriveItemType.Sound:
				return 'iconset_sound-bold';
			case Api.DriveItemType.Link:
			case Api.DriveItemType.ExternalLink:
				return 'iconset_link';
			case Api.DriveItemType.Gallery:
				return 'iconset_gallery-bold';
			default:
				return 'iconset_page-bold';
		}
	}

	static getDescription(driveItem: Api.DriveItem, galleryConfig: Api.Config): string {
		const locale = galleryConfig.locale;
		const descriptionKeys = Object.keys(driveItem.descriptions);

		if (!!descriptionKeys.length) {
			if (locale && driveItem.descriptions[locale]) {
				return driveItem.descriptions[locale];
			}
			return driveItem.descriptions[descriptionKeys[0]];
		}
		return '';
	}

	static getPublicationCover(path: string, page: number = 1): string {
		return path.replace(/{page}/, page.toString());
	}

	static isStored(itemId: number, storage: number[]): boolean {
		return storage.includes(itemId);
	}

	static getPageByDriveItemId(ressources: Api.DriveItemResource[], driveItemId: number): number {
		for (let i = 0; i < ressources.length; i++) {
			if (driveItemId === ressources[i].id) {
				return ( 1 + Math.floor( i / DRIVE_ITEM_LIST_PAGE_SIZE ) );
			}
		}
		return -1;
	}

	static containsString(driveItem: Api.DriveItem, searchString: string): boolean {
		const fieldsToCheck = [
			'label',
			'name',
			'descriptions',
			'tags',
			'titles'
		];
		const search = searchString.toLowerCase();

		for (let i = 0; i < fieldsToCheck.length; i++) {
			const field = fieldsToCheck[i];
			const value = driveItem[field];

			if (typeof(value) === 'object') {
				const subFields = Object.keys(value);

				for (let j = 0; j < subFields.length; j++) {
					const subField = subFields[j];
					if (value[subField].toString().toLowerCase().includes(search)) {
						return true;
					}
				}
			} else if (typeof(value) !== 'undefined') {
				if (value.toString().toLowerCase().includes(search)) {
					return true;
				}
			}
		}
		return false;
	}

	static isDownloadableItem(driveItemType: Api.DriveItemType): boolean {
		switch (driveItemType) {
			case Api.DriveItemType.File:
			case Api.DriveItemType.Image:
			case Api.DriveItemType.Movie:
			case Api.DriveItemType.Publication:
			case Api.DriveItemType.Sound:
			case Api.DriveItemType.Component:
			case Api.DriveItemType.Template:
				return true;
			case Api.DriveItemType.Directory:
			case Api.DriveItemType.V2Gateway:
			case Api.DriveItemType.Link:
			case Api.DriveItemType.AppContainer:
			case Api.DriveItemType.Gallery:
			case Api.DriveItemType.ExternalLink:
				return false;
		}
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/driveItem.ts